"use client";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { Loader2, Menu, X } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";
import LanguageToggle from "./LanguageToggle";

/* `key` is the stable identity — the label is looked up per language, so it
   can't be the React key or every row would remount on a language switch. */
const navLinks = [
  { key: "features", href: "/features" },
  { key: "pricing", href: "/pricing" },
  { key: "contact", href: "/contact" },
  { key: "blog", href: "/blog" },
  { key: "support", href: "/support" },
];

export default function Header() {
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loginHoldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();
  // Which drawer link is mid-navigation, so a slow route still gets a tap
  // acknowledgement instead of looking like a dead link.
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  const startLoginHold = () => {
    loginHoldTimer.current = setTimeout(() => {
      window.location.href = '/auth/superadmin-login';
    }, 5000);
  };
  const cancelLoginHold = () => {
    if (loginHoldTimer.current) {
      clearTimeout(loginHoldTimer.current);
      loginHoldTimer.current = null;
    }
  };

  // The header only cares about one bit — "are we past 20px" — but this used
  // to read `scrollY` and call `setScrolled` on *every* scroll event, which on
  // a touchscreen means once per frame for the whole length of a flick. Two
  // things were wrong with that on mobile:
  //
  //   - the listener was not passive, so the browser had to wait for it to
  //     return before it could scroll, putting React's render on the critical
  //     path of the gesture itself; and
  //   - it called setState every time, so a scroll from 500px to 3000px
  //     queued dozens of renders of the whole header that all produced
  //     identical markup.
  //
  // Now: passive, so scrolling never blocks on us; read inside rAF, so the
  // measurement is aligned to the frame and coalesced when events outrun it;
  // and the state is only touched when the boolean actually flips, so a long
  // scroll causes exactly one re-render instead of one per frame.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const next = window.scrollY > 20;
        setScrolled((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1280) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close the drawer when the route actually commits — NOT in the link's own
  // onClick. Closing on click unmounts the drawer while the App Router is still
  // fetching the destination, so on a slow route the menu vanishes and the
  // visitor is left staring at the page they came from, which reads as "that
  // link took me to the wrong page". Waiting for the commit means the menu is
  // visible for exactly as long as the navigation is pending.
  useEffect(() => {
    setMobileOpen(false);
    setPendingHref(null);
  }, [pathname]);

  // Freeze the page behind the mobile drawer. Pinning the body with a negative
  // top (instead of just overflow:hidden) is what stops iOS Safari from
  // scrolling the page under the overlay, so the scroll offset has to be
  // restored by hand on close.
  useEffect(() => {
    if (!mobileOpen) return;
    const body = document.body;
    const scrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
    };
  }, [mobileOpen]);

  // On the home page the logo points at the route it is already on, and the
  // App Router treats "/" -> "/" as a no-op: nothing re-renders and the
  // viewport stays put, so the click reads as a dead link. Scroll to the top
  // by hand instead. Off the home page the Link navigates as normal.
  const onLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (pathname !== "/") return;
    // Let cmd/ctrl/shift-click still open the home page in a new tab or window.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    e.preventDefault();
    const behavior: ScrollBehavior =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

    if (mobileOpen) {
      // The open drawer pins <body> at a negative top, and its cleanup restores
      // the saved offset on close — running the scroll now would be undone a
      // moment later. Close first, then scroll once that cleanup has committed.
      setMobileOpen(false);
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior }));
      return;
    }
    window.scrollTo({ top: 0, behavior });
  };

  // With the drawer open the header has to be a flat opaque bar: the floating
  // pill leaves a transparent gap around itself that the page shows through.
  const pill = scrolled && !mobileOpen;

  return (
    <>
      <header className={`w-full fixed top-0 left-0 z-50 transition-all duration-300 ${pill ? "pt-1 sm:pt-2" : ""} ${mobileOpen ? "bg-white border-b border-[#E8F4FD]" : ""}`}>
        {/* The pill spans the shared content width, so the logo and nav line up
            with every section below. Inner padding only appears once the pill
            has a background — otherwise the logo would sit off the page grid. */}
        <div className="site-container">
          <div className={`w-full flex items-center justify-between h-14 sm:h-16 lg:h-[68px] transition-all duration-300 ${pill ? "bg-white/95 backdrop-blur-xl shadow-[0_8px_30px_rgba(61,94,225,0.12)] rounded-full border border-brand-100/50 px-3.5 sm:px-6 lg:px-8" : "bg-transparent"}`}>

            {/* Logo */}
            <Link href="/" onClick={onLogoClick} className="relative flex-shrink-0 w-[112px] sm:w-[180px] lg:w-[190px] h-[26px] sm:h-10 lg:h-10 notranslate" translate="no" aria-label="Kaympus Home">
              <Image src="/logo.png" alt="Kaympus" fill sizes="(max-width: 640px) 112px, 190px" className="object-contain object-left" priority />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden xl:flex items-center gap-8 xl:gap-10">
              {navLinks.map(l => (
                <Link key={l.key} href={l.href}
                  className="text-[14px] font-semibold text-[#4a5568] hover:text-[#3D5EE1] transition-colors duration-200 whitespace-nowrap">
                  {t(`nav.${l.key}`)}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
              {/* Ahead of Login rather than after Free Trial: the two buttons on
                  the right are the conversion path and shouldn't be pushed off
                  the end of the bar by a utility control. */}
              <LanguageToggle />
              <a href="/auth/sign-in"
                onMouseDown={startLoginHold} onMouseUp={cancelLoginHold} onMouseLeave={cancelLoginHold}
                onTouchStart={startLoginHold} onTouchEnd={cancelLoginHold}
                className="inline-flex items-center px-5 py-2.5 rounded-xl text-[14px] font-bold text-[#3D5EE1] border border-[#3D5EE1]/30 hover:border-[#3D5EE1] hover:bg-[#EFF3FE] transition-all duration-200 whitespace-nowrap">
                {t("nav.login")}
              </a>
              <Link href="/contact"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-[14px] font-bold text-white btn-glow whitespace-nowrap"
                style={{ background: "linear-gradient(135deg, #3D5EE1, #2A48C9)" }}>
                {t("nav.freeTrial")}
              </Link>
            </div>

            {/* Mobile: language switch + hamburger. The switcher sits outside
                the drawer so a Hindi reader can find it without first working
                out that the hamburger hides it. */}
            <div className="xl:hidden flex items-center gap-1.5 sm:gap-2">
              <LanguageToggle />
              <button
                className="p-1.5 sm:p-2 rounded-lg hover:bg-[#EFF3FE] transition-colors"
                onClick={() => setMobileOpen(v => !v)}
                aria-label={t("nav.toggleMenu")}
                aria-expanded={mobileOpen}
              >
                {mobileOpen
                  ? <X className="w-[18px] h-[18px] sm:w-[22px] sm:h-[22px] text-[#0d1117]" />
                  : <Menu className="w-[18px] h-[18px] sm:w-[22px] sm:h-[22px] text-[#0d1117]" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="xl:hidden fixed inset-x-0 top-14 sm:top-16 lg:top-[68px] bottom-0 z-40 bg-white flex flex-col shadow-xl overflow-y-auto overscroll-contain">
          {/* Nav links */}
          <nav className="flex flex-col px-4 pt-4 gap-1">
            {navLinks.map(l => (
              <Link
                key={l.key}
                href={l.href}
                // Tapping the page you're already on produces no route change,
                // so the commit effect above never fires — close it here.
                onClick={() => (l.href === pathname ? setMobileOpen(false) : setPendingHref(l.href))}
                aria-current={l.href === pathname ? "page" : undefined}
                className={`flex items-center justify-between py-3.5 px-4 text-[16px] font-semibold rounded-xl transition-all duration-200 ${pendingHref === l.href || l.href === pathname
                  ? "bg-[#EDF0FC] text-[#1B35AC]"
                  : "text-[#0d1117] hover:bg-[#EDF0FC] hover:text-[#1B35AC]"}`}
              >
                {t(`nav.${l.key}`)}
                {pendingHref === l.href && <Loader2 size={16} className="animate-spin text-[#3D5EE1]" />}
              </Link>
            ))}
          </nav>

          {/* mt-auto pins the divider + CTAs to the bottom of the drawer, so
              the sheet reads as a composed panel instead of links floating in
              the top third with dead space below. */}
          <div className="mx-4 mb-4 mt-auto border-t border-[#E8F4FD]" />

          {/* CTA buttons */}
          <div className="px-4 pb-8 flex flex-col gap-3">
            <a href="/auth/sign-in"
              onMouseDown={startLoginHold} onMouseUp={cancelLoginHold} onMouseLeave={cancelLoginHold}
              onTouchStart={startLoginHold} onTouchEnd={cancelLoginHold}
              className="py-3.5 px-4 text-[15px] font-semibold text-center text-[#3D5EE1] border border-[#D6E0FC] rounded-xl hover:bg-[#EFF3FE] transition-all duration-200">
              {t("nav.login")}
            </a>
            <Link href="/contact"
              // Distinct key from the "Contact" nav row so tapping one doesn't
              // spin both — they share a destination.
              onClick={() => (pathname === "/contact" ? setMobileOpen(false) : setPendingHref("cta:/contact"))}
              className="flex items-center justify-center gap-2 py-3.5 px-4 text-[15px] font-bold text-center text-white rounded-xl btn-glow"
              style={{ background: "linear-gradient(135deg, #3D5EE1, #2A48C9)" }}>
              {t("nav.startFreeTrial")}
              {pendingHref === "cta:/contact" && <Loader2 size={16} className="animate-spin" />}
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
